import React from "react";
import { Button, Space } from "antd";
import {
  PlusOutlined,
  UndoOutlined,
  SaveOutlined,
  EyeOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

/**
 * @typedef {Object} ToolbarActionsProps
 * @property {Function} onAddField - Callback function to handle adding a new field
 * @property {Function} onUndo - Callback function to handle undoing the last action
 * @property {Function} onSave - Callback function to handle saving the configuration to form
 * @property {Function} onDownloadConfig - Callback function to handle downloading the configuration
 * @property {Function} onShowConfig - Callback function to display the current configuration
 * @property {number} currentPage - The current page number being displayed
 * @property {number} totalPages - Total number of pages in the document
 * @property {Function} onPageChange - Callback function for handling page navigation
 * @property {boolean} hasFields - Flag indicating whether there are any fields configured
 * @property {boolean} isFormContext - Flag indicating whether component is used in form context
 * @property {Object} intl - Internationalization object for translations
 */

/**
 * A toolbar component providing actions for PDF field configuration.
 * Includes buttons for adding fields, undoing actions, saving configuration,
 * viewing current configuration, and navigating between pages.
 *
 * @component
 * @param {ToolbarActionsProps} props - Component properties
 *
 * @example
 * ```jsx
 * <ToolbarActions
 *   onAddField={() => handleAddField()}
 *   onUndo={() => handleUndo()}
 *   onSave={() => handleSave()}
 *   onDownloadConfig={() => handleDownloadConfig()}
 *   onShowConfig={() => showConfiguration()}
 *   currentPage={1}
 *   totalPages={5}
 *   onPageChange={(direction) => handlePageChange(direction)}
 *   hasFields={true}
 *   isFormContext={true}
 *   intl={intlObject}
 * />
 * ```
 */
const ToolbarActions = ({
  onAddField,
  onUndo,
  onSave,
  onDownloadConfig,
  onShowConfig,
  currentPage,
  totalPages,
  onPageChange,
  hasFields,
  isFormContext,
  intl,
}) => (
  <>
    <Space>
      <Button icon={<PlusOutlined />} onClick={onAddField} type="primary">
        {intl.formatMessage({ id: "app.settings.forms.add_field" })}
      </Button>
      <Button icon={<UndoOutlined />} onClick={onUndo} disabled={!hasFields}>
        {intl.formatMessage({ id: "app.settings.forms.undo_last" })}
      </Button>
      {isFormContext && (
        <Button
          icon={<SaveOutlined />}
          onClick={onSave}
          disabled={!hasFields}
          type="primary"
        >
          {intl.formatMessage({ id: "app.settings.forms.save_configuration" })}
        </Button>
      )}
      <Button
        icon={<DownloadOutlined />}
        onClick={onDownloadConfig}
        disabled={!hasFields}
      >
        {intl.formatMessage({
          id: "app.settings.forms.download_configuration",
        })}
      </Button>
      <Button
        icon={<EyeOutlined />}
        onClick={onShowConfig}
        disabled={!hasFields}
      >
        {intl.formatMessage({ id: "app.settings.forms.show_configuration" })}
      </Button>
    </Space>

    <Space
      style={{
        width: "100%",
        justifyContent: "center",
        marginBottom: 16,
        paddingTop: 16,
      }}
    >
      <Button
        icon={<RightOutlined />}
        onClick={() => onPageChange("prev")}
        disabled={currentPage <= 1}
      >
        {" "}
        {intl.formatMessage({ id: "app.settings.forms.previous" })}
      </Button>
      <span>
        {intl.formatMessage({ id: "app.settings.forms.page" })} {currentPage}{" "}
        {intl.formatMessage({ id: "app.settings.forms.of" })} {totalPages}
      </span>
      <Button
        onClick={() => onPageChange("next")}
        disabled={currentPage >= totalPages}
      >
        {intl.formatMessage({ id: "app.settings.forms.next" })} <LeftOutlined />
      </Button>
    </Space>
  </>
);

export default ToolbarActions;
